/**
 * UseCaseTemplateBrowser
 *
 * Read-only browser for use-case assessment templates. Displays domain and
 * risk-level badges alongside an intake question count badge.
 *
 * @version 1.0.0
 * @date 2026-03-26
 */

import React, { useState, useEffect } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { BrowserLayout } from './BrowserLayout';

// ============================================================================
// TYPES
// ============================================================================

interface UseCaseTemplate {
  id: string;
  name: string;
  slug: string;
  description: string;
  domain: string;
  category: string;
  risk_level: string;
  intake_questions?: unknown[];
}

interface ApiResponse {
  templates?: UseCaseTemplate[];
}

// ============================================================================
// HELPERS
// ============================================================================

type BadgeVariant = 'badge-red' | 'badge-amber' | 'badge-blue' | 'badge-green';

function riskBadgeClass(risk: string): BadgeVariant {
  switch (risk.toLowerCase()) {
    case 'critical':
      return 'badge-red';
    case 'high':
      return 'badge-amber';
    case 'low':
      return 'badge-green';
    default:
      return 'badge-blue';
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export const UseCaseTemplateBrowser: React.FC = () => {
  const { authFetch } = useApiClient();

  const [templates, setTemplates] = useState<UseCaseTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch('/api/v1/use-case-templates');
        if (!response.ok) {
          throw new Error(`Failed to load use-case templates (HTTP ${response.status})`);
        }
        const data: ApiResponse | UseCaseTemplate[] = await response.json();
        const raw: UseCaseTemplate[] = Array.isArray(data)
          ? data
          : (data as ApiResponse).templates ?? [];
        if (!cancelled) {
          setTemplates(raw);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unexpected error loading templates.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  // ---- Render states -------------------------------------------------------

  if (loading) {
    return (
      <BrowserLayout
        title="Use Case Templates"
        description="Pre-configured assessment templates for common pharma use cases with station configs and intake questions."
      >
        <div className="browser-loading">
          <div className="spinner" />
          Loading use case templates…
        </div>
      </BrowserLayout>
    );
  }

  if (error) {
    return (
      <BrowserLayout
        title="Use Case Templates"
        description="Pre-configured assessment templates for common pharma use cases with station configs and intake questions."
      >
        <div className="browser-error">
          <div className="error-icon">⚠</div>
          <p>{error}</p>
        </div>
      </BrowserLayout>
    );
  }

  // ---- Main render ---------------------------------------------------------

  return (
    <BrowserLayout
      title="Use Case Templates"
      description="Pre-configured assessment templates for common pharma use cases with station configs and intake questions."
    >
      <div className="template-card-grid">
        {templates.map((template) => {
          const questionCount = template.intake_questions?.length ?? 0;
          const riskClass = riskBadgeClass(template.risk_level);

          return (
            <div key={template.id} className="template-card">
              <h3>{template.name}</h3>
              <p className="template-card-description">{template.description}</p>
              <div className="template-card-badges">
                <span className="badge badge-blue">{template.domain}</span>
                <span className={`badge ${riskClass}`}>{template.risk_level}</span>
                <span className="badge badge-gray">
                  {questionCount} {questionCount === 1 ? 'question' : 'questions'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </BrowserLayout>
  );
};

export default UseCaseTemplateBrowser;
